/**
 * Rank arithmetic for drag-reordering the backlog.
 *
 * `rank` is an ordinal only — nobody reads the numbers, they just have to
 * sort the way the board looks. So the goal is always the smallest set of
 * rows to rewrite, not tidy numbering.
 *
 * Two strategies, in order of preference:
 *
 *   1. Slot the moved rows into the gap between their new neighbours, so
 *      only the moved rows are written. Ranks are gapped by 10 on import
 *      precisely to leave room for this.
 *   2. If the gap is too tight, renumber the whole group 10, 20, 30… and
 *      write every row whose rank actually changed.
 *
 * Inserting at the front never needs strategy 2: the moved rows just take
 * ranks below the current minimum, and negative ranks are perfectly fine
 * for an ordinal.
 */

export const STEP = 10

/**
 * @param {Array<{id:number, rank:number}>} rows  group contents, any order
 * @param {Array<number>} movedIds  rows being moved, in the order they should end up
 * @param {number|null} beforeId  insert before this row, or null for the front
 * @returns {Array<{id:number, rank:number}>} only the rows that need writing
 */
export function planReorder(rows, movedIds, beforeId = null) {
  const moved = new Set(movedIds)

  // Everything not moving, in its current visual order.
  const rest = [...rows]
    .filter(r => !moved.has(r.id))
    .sort((a, b) => a.rank - b.rank || a.id - b.id)

  // The moved rows keep their relative order to each other.
  const byId = new Map(rows.map(r => [r.id, r]))
  const movingRows = movedIds.map(id => byId.get(id)).filter(Boolean)
  if (!movingRows.length) return []

  // Dropping onto a row that is itself being dragged has no meaning —
  // most obviously when a single row is dropped on itself. Treat it as a
  // no-op rather than inventing a destination.
  if (beforeId !== null && moved.has(beforeId)) return []

  let index = 0
  if (beforeId !== null) {
    const found = rest.findIndex(r => r.id === beforeId)
    // An unknown target (filtered out, or deleted by someone else) falls
    // back to the front rather than silently appending at the end.
    index = found === -1 ? 0 : found
  }

  const before = index > 0 ? rest[index - 1] : null
  const after = index < rest.length ? rest[index] : null
  const count = movingRows.length

  // --- strategy 1: fit in the gap --------------------------------------

  if (before === null && after === null) {
    return movingRows.map((row, i) => ({ id: row.id, rank: (i + 1) * STEP }))
  }

  if (before === null) {
    // Front of the group: descend below the current minimum.
    return movingRows.map((row, i) => ({
      id: row.id,
      rank: after.rank - (count - i) * STEP,
    }))
  }

  if (after === null) {
    // End of the group: ascend above the current maximum.
    return movingRows.map((row, i) => ({
      id: row.id,
      rank: before.rank + (i + 1) * STEP,
    }))
  }

  const room = after.rank - before.rank - 1
  if (room >= count) {
    const stride = Math.floor((after.rank - before.rank) / (count + 1))
    return movingRows.map((row, i) => ({
      id: row.id,
      rank: before.rank + stride * (i + 1),
    }))
  }

  // --- strategy 2: renumber the group ----------------------------------

  const ordered = [
    ...rest.slice(0, index),
    ...movingRows,
    ...rest.slice(index),
  ]
  const writes = []
  ordered.forEach((row, i) => {
    const rank = (i + 1) * STEP
    if (row.rank !== rank) writes.push({ id: row.id, rank })
  })
  return writes
}

/**
 * Ranks that put the moved rows at the front of a sprint they are joining.
 *
 * Used for cross-sprint drops, where the rule is always "front of the new
 * sprint" regardless of where in the group the row was dropped.
 *
 * @param {Array<{id:number, rank:number}>} targetRows  destination contents
 * @param {Array<number>} movedIds  in the order they should end up
 */
export function planMoveToFront(targetRows, movedIds) {
  if (!movedIds.length) return []
  const min = targetRows.length
    ? Math.min(...targetRows.map(r => r.rank))
    : STEP * (movedIds.length + 1)
  const count = movedIds.length
  return movedIds.map((id, i) => ({ id, rank: min - (count - i) * STEP }))
}

/**
 * The rows to drag when a drag starts on `row`.
 *
 * Dragging a selected row drags the whole selection; dragging an unselected
 * row drags just that row and leaves the selection alone, which is what
 * every file manager does.
 */
export function dragSet(row, selectedIds, orderedRows) {
  if (!selectedIds.has(row.id)) return [row.id]
  return orderedRows.filter(r => selectedIds.has(r.id)).map(r => r.id)
}
